import { EventRequest, EventResource, EventStatus } from '@/api/requests/calendar/types';
import moment from 'moment';
import { AddressRequest } from '@/api/requests/contact/types';

export interface EventInterface {
    allDay: boolean;
    attendees?: Array<string | number>;
    busy?: boolean;
    calendarId: string;
    workId?: string | number | null;
    taskId?: string | number | null;
    vacationId?: number | null;
    end: Date;
    location?: AddressRequest | null;
    id?: string | number;
    description?: string | null;
    start: Date;
    private?: boolean;
    status?: EventStatus | null;
    type?: string | null;
    typeDescription?: string | null;
    typeExtendedDescription?: string | null;
    summary?: string | null;

}

export default class Event implements EventInterface {
    /**
     * Default event duration.
     */
    static readonly DEFAULT_DURATION = moment.duration(30, 'm');

    /**
     * Initialize event object from API JSON resource.
     *
     * @param data
     */
    static fromResource(data: EventResource) {
        return new this({
            ...data,
            start: new Date(data.start),
            end: new Date(data.end),
            attendees: data.attendees && data.attendees.length > 0  ? data.attendees.map((profile) => profile.id) : [],
            workId: data.work ? data.work.id : null,
            taskId: data.work ? data.task.id : null,
            vacationId: data.vacation ? data.vacation.id : null,
        });
    }

    /**
     * @var
     */
    allDay: boolean = false;

    /**
     * @var
     */
    attendees: Array<string | number> = [];

    /**
     * @var
     */
    busy: boolean = true;

    /**
     * @var
     */
    calendarId: string = '';

    /**
     * @var
     */
    workId?: string | number | null = null;

    /**
     * @var
     */
    taskId?: string | number | null = null;

    /**
     * @var
     */
    vacationId?: number | null = null;

    /**
     * @var
     */
    duration: moment.Duration = Event.DEFAULT_DURATION;

    /**
     * @var
     */
    id?: string | number;

    /**
     * @var
     */
    location?: AddressRequest;

    /**
     * @var
     */
    private: boolean = false;

    /**
     * @var
     */
    status: EventStatus | null = null;

    /**
     * @var
     */
    type: string | null = null;

    /**
     * @var
     */
    typeDescription: string | null = null;

    /**
     * @var
     */
    typeExtendedDescription: string | null = null;

    /**
     * @var
     */
    description: string | null = null;

    /**
     * @var
     */
    summary: string | null = null;

    /**
     * @var
     */
    private _end: moment.Moment = moment();

    /**
     * @var
     */
    private _start: moment.Moment = moment();

    /**
     * Get end date.
     */
    get endDate(): Date {
        return this.end;
    }

    /**
     * Set end date.
     *
     * @param date
     */
    set endDate(date: Date) {
        const temp = moment(date);
        const newDate = temp.hours(this._end.hours()).minutes(this._end.minutes());
        this.end = newDate.toDate();
    }

    /**
     * Get start time.
     */
    get endTime(): string {
        return moment(this.end).format('HH:mm');
    }

    /**
     * Set end time.
     *
     * @param time
     */
    set endTime(time: string) {
        const temp = moment(time, 'HH:mm');
        const date = moment(this.end).hours(temp.hours()).minutes(temp.minutes());
        this.end = date.toDate();
    }

    /**
     * Get start date.
     */
    get startDate(): Date {
        return this.start;
    }

    /**
     * Set start date.
     *
     * @param date
     */
    set startDate(date: Date) {
        const temp = moment(date);
        const newDate = temp.hours(this._start.hours()).minutes(this._start.minutes());
        this.start = newDate.toDate();
    }

    /**
     * Get start time.
     */
    get startTime(): string {
        return moment(this.start).format('HH:mm');
    }

    /**
     * Set start time.
     *
     * @param time
     */
    set startTime(time: string) {
        const temp = moment(time, 'HH:mm');
        const date = moment(this.start).hours(temp.hours()).minutes(temp.minutes());
        this.start = date.toDate();
    }

    /**
     * @var
     */
    get end(): Date {
        return this._end.toDate();
    }

    /**
     * @var
     */
    set end(date: Date) {
        this._end = moment(date);
        this.duration = moment.duration(this._end.diff(this._start));
    }

    /**
     * @var
     */
    get start(): Date {
        return this._start.toDate();
    }

    /**
     * @var
     */
    set start(date: Date) {
        this._start = moment(date);
        this.end = moment(this._start).add(this.duration).toDate();
    }

    /**
     * Event class constructor.
     *
     * @param initializer
     */
    constructor(initializer: Partial<EventInterface>) {
        Object.assign(this, initializer);
    }

    toRequest(): EventRequest {
        return {
            ...this,
            start: moment(this.start).format('YYYY-MM-DD HH:mm:ss'),
            end: moment(this.end).format('YYYY-MM-DD HH:mm:ss'),
        };
    }

    setTitle(type: string): string {
        switch (type) {
            case 'smart_working':
                return 'Smart Working';
            case 'not_paid_time_off':
                return 'Permesso non retribuito';
            case 'paid_time_off':
                return 'Permesso retribuito';
            case 'vacations_and_permissions':
                return 'Ferie e permessi';
            case 'wedding':
                return 'Matrimonio';
            case 'marriage_license':
                return 'Licenza Matrimoniale';
            case 'funeral':
                return 'Funerale';
            case 'medical_exam':
                return 'Visite Mediche';
            case 'military_service':
                return 'Servizio militare';
            case 'leave_of_absence':
                return 'Periodo di aspettativa';
            case 'others':
                return 'Altro';
            case 'pregnancy':
                return 'Maternità';
            case 'illness':
                return 'Malattia';
            case 'mission':
                return 'Missione';
            case 'permission':
                return 'Permesso monte ore';
            case 'relocation':
                return 'Trasloco';
            case 'vacation':
                return 'Ferie';
            case 'ex_holiday':
                return 'Ex-festività';
            default:
                return '';
        }
    }

}
